// src/context/AuthContext.js [marker: permissions-real v1]
import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [perms, setPerms] = useState(null); // { 'members.view': true, ... } — what this person may do

  // load the signed-in person's permissions whenever the user changes (and every 2 minutes, so changes show up)
  useEffect(() => {
    if (!user) { setPerms(null); return undefined; }
    let alive = true;
    const load = () => api.get('/permissions/my').then(r => { if (alive) setPerms(r.data.permissions || {}); }).catch(() => { if (alive) setPerms(p => p || {}); });
    load();
    const t = setInterval(load, 2 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, [user]);

  // On mount, check if we already have a token and fetch user
  useEffect(() => {
    const token = localStorage.getItem('njjba_access_token');
    if (token) {
      api.get('/auth/staff/me')
        .then(res => setUser(res.data))
        .catch(() => {
          localStorage.removeItem('njjba_access_token');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Called after token is already saved to localStorage by LoginPage
  // Just fetches the user profile to populate context
  const loadUser = async () => {
    try {
      const res = await api.get('/auth/staff/me');
      setUser(res.data);
      return res.data;
    } catch (err) {
      setUser(null);
      throw err;
    }
  };

  // Legacy login (used by older code paths)
  const login = async (email, password) => {
    const res = await api.post('/auth/staff/login', { email, password });
    if (res.data.accessToken) {
      localStorage.setItem('njjba_access_token', res.data.accessToken);
      setUser(res.data.user);
    }
    return res.data;
  };

  const logout = async () => {
    try { await api.post('/auth/staff/logout'); } catch (e) {}
    localStorage.removeItem('njjba_access_token');
    localStorage.removeItem('njjba_device_token');
    setUser(null);
  };

  const isSuperAdmin     = () => user?.role === 'super_admin';
  const isManagerOrAbove = () => ['super_admin','manager'].includes(user?.role);
  const isFinanceOrAbove = () => ['super_admin','finance'].includes(user?.role);
  // can('members.view') or can(['events.view', 'events.checkin']) — any of them
  const can = (keys) => {
    if (user?.role === 'super_admin') return true;
    if (!perms) return false;
    return (Array.isArray(keys) ? keys : [keys]).some(k => !!perms[k]);
  };
  const permsLoaded = user?.role === 'super_admin' || perms !== null;

  return (
    <AuthContext.Provider value={{ user, loading, login, loadUser, logout, isSuperAdmin, isManagerOrAbove, isFinanceOrAbove, can, permsLoaded, perms }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
